import { FeatureFlagIds } from "./featureFlagIds";
import { IApplication } from "../Interfaces/Application/IApplication";
import { ICustomField } from "../Interfaces/Common/ICustomField";
import { v4 } from 'uuid';
import { IAppCustomer } from "../Interfaces/Application/IAppCustomer";
import { IEmployee } from "../Interfaces/Admin/IEmployee";
import { IConfiguration } from "../Data/ConfigurationData";
import { ICustomer } from "../Interfaces/Customer/ICustomer";
import differenceInCalendarYears from "date-fns/differenceInCalendarYears";
import { ICustomerRole } from "../Interfaces/Customer/ICustomerRole";
import { ApplicationDispatch } from "../store/application/types";
import { BusinessRulesEngine } from "../Data/BusinessRulesData";
import { updateEditingApp } from "../store/application/actions";
import { applicationStatusesAfterDeskingToAms, Constants } from "./constants";
import { all, includes, isEmpty, isNil, isNotNil, sort } from 'ramda';
import { Customer } from '../Interfaces/Customer/ICustomerConfig';
import { IStipulation, IStipulationCategory } from "../Interfaces/StipulationTypes";
import { IApplicationAssignment } from "../Interfaces/Application/IApplicationAssignment";
import { IEmployeeRoleChecklist } from "../Interfaces/Common/IEmployeeRoleChecklist";
import { IApplicationChecklist } from "../Interfaces/Application/IApplicationChecklist";
import { IApplicationException } from "../Interfaces/Application/IApplicationException";
import { IState } from "../Interfaces/Common/IState";
import { IApplicationLeaseQuote } from "../Interfaces/Application/IApplicationLeaseQuote";
import { ICustomValuePair } from "../Interfaces/Application/ICustomValuePair";
import { IChecklist } from "../Interfaces/Common/IChecklist";
import { getNameForCustomerRole } from "./utils";
import { IInvalidAppCustomerStipulationGroup } from "../Interfaces/Application/IInvalidAppCustomerStipulation";
import { IApplicationRowChecklist } from "../Interfaces/Application/IApplicationRowChecklist";
import { intersection } from "lodash";
import { IEntityCustomField } from "../Interfaces/Application/IEntityCustomField";
import { IRuleViolation } from "../Interfaces/Application/IApplicationQuote";
import { getMonthsElapsed, hasValue, isValidSocialSecurityNumber } from "@amsanalytics/react-shared-library"
import produce from "immer";
import { ICustomerEmployment } from "../Interfaces/Customer/ICustomerEmployment";
import { ICustomerAddress } from "../Interfaces/Customer/ICustomerAddress";
import { IndirectQuoteType } from "../Data/IndirectQuoteType";
import { getAppQuoteByType } from "../Components/Quoters/IndirectQuoter/IndirectQuoterUtils";
import { Organization } from "@amsanalytics/react-shared-library/build/Models/Organization";

const primaryRoleName: string = "Applicant";
const cb1RoleName: string = "Co-buyer 1";
const cb2RoleName: string = "Co-buyer 2";

export const canApproveDenyApp = (app: IApplication, isApproval: boolean, useInternalAmsScore: boolean | undefined): string[] => {
  // re: issue #62 https://github.com/bbuchanan/los-issues/issues/62
  // Mike says:
  //Looks like I can approve and declines deals without entering a structure.
  // In order for a deal to be approved, we need these items to be true.

  // Employer Information must be entered
  // A vehicle with sales prices >0 must be selected.
  // All the fields on the quote page must be populated with some value.
  // Most importantly, the deal must be scored with a value greater than 0.
  // In order to get to the approved status, those four things must be true.
  const errors: string[] = [];
  const primaryCustomer: IAppCustomer = app.appCustomers.find(
    x => x.customerRole.isPrimaryApplicant
  )!;

  const underwriterAppQuote = getAppQuoteByType(app, IndirectQuoteType.Underwriter);

  const quote = underwriterAppQuote?.quote;
  if (!primaryCustomer.customer.customerEmployment) {
    errors.push("Primary applicant has no employer information");
  }

  // if (!app.vehicleId) {
  //   errors.push("No vehicle selected");
  // }

  if (!quote) {
    return errors; /* quit early */
  }

  if (
    quote.amountToFinance === undefined ||
    quote.apr === undefined ||
    //quote.carSalePrice === undefined ||
    quote.cashDown === undefined ||
    quote.deferredDown === undefined ||
    quote.discount === undefined ||
    quote.fees === undefined ||
    quote.nadavalue === undefined ||
    quote.other === undefined ||
    quote.taxes === undefined ||
    quote.tradeAllowance === undefined ||
    quote.tradeLien === undefined
  ) {
    errors.push(
      "All items on the quote tab must have a value before approving"
    );
  }

  // looks like car sale price is not editable, so we're using the price on the vehicle object, therefore, there will
  // always be a price.
  //if ((quote.carSalePrice ?? 0) <= 0) {
  //  errors.push("Car price must be greater than 0.");
  //}

  if (isApproval && useInternalAmsScore && !quote.amsscore) {
    errors.push("Score must be greater than 0 to approve or deny.");
  }

  return errors;
};

export const isAdmin = (loggedInUser?: IEmployee): boolean => {
  // const isAdmin = loggedInUser && (
  //   loggedInUser.employeeRole?.name === 'Admin'
  //   || !!loggedInUser.employeeEmployeeRoles?.find(x => x.employeeRole?.name === 'Admin')
  // );
  // return isAdmin;
  return isInRole(Constants.roles.administratorId, loggedInUser);
}

export const isUnderwriter = (loggedInUser?: IEmployee): boolean => {
  // const isUnderwriter = loggedInUser && (
  //   loggedInUser.employeeRole?.name === 'Underwriter'
  //   || !!loggedInUser.employeeEmployeeRoles?.find(x => x.employeeRole?.name === 'Underwriter')
  // );
  // return isUnderwriter;
  return isInRole(Constants.roles.underwriterId, loggedInUser);
}

export const isUnderwriterManager = (employee?: IEmployee): boolean => {
  return isInRole(Constants.roles.creditManagerId, employee);
}

export const isApplicationUnderwriter = (editingApp: IApplication, loggedInUser?: IEmployee) => {
  // return loggedInUser && loggedInUser.employeeRoleId === editingApp.underwriter?.employeeRoleId
  // && loggedInUser.id.toUpperCase() === editingApp.underwriter?.id.toUpperCase();

  return loggedInUser && isUnderwriter(loggedInUser)
    && loggedInUser.id.toUpperCase() === editingApp.underwriter?.id.toUpperCase();
}

export const isSalesPerson = (loggedInUser?: IEmployee) => {
  return isInRole(Constants.roles.salespersonId, loggedInUser);
}

export const isDealer = (loggedInUser?: IEmployee) => {
  return isInRole(Constants.roles.dealerId, loggedInUser);
}

export const isApplicationSalesperson = (editingApp: IApplication, loggedInUser?: IEmployee): boolean => {
  // return loggedInUser && loggedInUser.employeeRoleId === editingApp.salesperson?.employeeRoleId
  // && loggedInUser.id.toUpperCase() === editingApp.underwriter?.id.toUpperCase();

  return isSalesPerson(loggedInUser)
    && (loggedInUser?.id.toUpperCase() === getAssignedEmployeeByRoleAndSequence(editingApp.applicationAssignments, Constants.roles.salespersonId, 1)?.assignedToId?.toUpperCase()
      || loggedInUser?.id.toUpperCase() === getAssignedEmployeeByRoleAndSequence(editingApp.applicationAssignments, Constants.roles.salespersonId, 2)?.assignedToId?.toUpperCase());
}

export const canPullCredit = (configuration: IConfiguration, loggedInUser?: IEmployee) => {
  return canUnderwrite(loggedInUser)
    || (configuration.clientId === 'TAC' && isSalesPerson(loggedInUser));
}

export const canUnderwrite = (loggedInUser?: IEmployee) => {
  const admin = isAdmin(loggedInUser);
  const underwriter = isUnderwriter(loggedInUser);
  const creditManager = isInRole(Constants.roles.creditManagerId, loggedInUser);
  return admin || underwriter || creditManager;
}

export const canViewQuote = (app?: IApplication) => {
  const appStatusName = app?.appStatus?.name;
  const result = appStatusName === "Approved"
    || appStatusName === "Conditionally Approved"
    || appStatusName === "Conditions Tentatively Satisfied"
    || appStatusName === "Desking";
  return result;
}

export const canUpdateApp = (
  app: IApplication,
  loggedInUser: IEmployee
): boolean => {
  let canUpdate: boolean = true;

  if (app?.unlockedBySuperUser || app?.activeUnlock) {
    return true;
  }

  // https://github.com/bbuchanan/los-issues/issues/41
  // conditions where we should not allow an update
  // If logged in as a SalesPerson, then the app cannot be editable if it is in the following status:
  //  Underwriting
  //  Desked

  // If logged in as an Underwriter, then the app can't be edited if it is in the following status:
  //  Sales
  //  Desked
  if (!app?.appStatus?.editable) {
    // Desked app status is set as not editable across the board.
    return false;
  }

  // if (loggedInUser.employeeRole?.name === "Salesperson") {
  //   if (app.appStatus?.name === "Underwriting") {
  //     return false;
  //   }
  // }

  if (isSalesPerson(loggedInUser) && app.appStatusId.toLowerCase() === Constants.applicationStatuses.underwriting.toLowerCase()) {
    return false;
  }

  // if (loggedInUser.employeeRole?.name === "Underwriter") {
  //   if (app.appStatus.name === "Sales") {
  //     return false;
  //   }
  // }

  if (isUnderwriter(loggedInUser) && app.appStatusId.toLowerCase() === Constants.applicationStatuses.sales.toLowerCase()) {
    return false;
  }

  return canUpdate;
};

export const validateApp = (app: IApplication): string[] => {
  const errors: string[] = [];

  const underwriterAppQuote = getAppQuoteByType(app, IndirectQuoteType.Underwriter);

  if (
    (underwriterAppQuote?.quote?.apr ?? 0) > 100 ||
    (underwriterAppQuote?.quote?.apr ?? 0) < 0
  ) {
    errors.push("APR cannot be less than zero or greater than 100");
  }
  return errors;
};

// Looks at each AppCustomer-stipulation to make sure that they have at least 1 document associated with the stipulation
export const validateStipulations = (appCustomers: IAppCustomer[], stipulationCategories: IStipulationCategory[]) => {
  if (appCustomers.length === 0) {
    return ['No customers exist for this application.'];
  }
  const errors: string[] = [];

  // Initialize list of invalidAppCustomerStipGroups
  const invalidAppCustomerStipGroups: IInvalidAppCustomerStipulationGroup[] = appCustomers.map(ac => ({ appCustomerId: ac.id, appCustomerRoleName: getNameForCustomerRole(ac.customerRoleId), invalidStipulationCategories: [] }));
  // For all appCustomerStipulations: check if the stipulation is in verified or exeption status. If not, it is invalid.
  invalidAppCustomerStipGroups.forEach((invalidGroup: IInvalidAppCustomerStipulationGroup) => {
    stipulationCategories.forEach((stipCategory: IStipulationCategory) => {
      const stipsForCategoryAndCustomer: IStipulation[] = stipCategory.stipulations.filter(s => s.appCustomerStipulations.some(acs => acs.appCustomerId === invalidGroup.appCustomerId));
      // Check if this stipulation exists for the customer we are currently iterating over.
      if (stipsForCategoryAndCustomer.length > 0) {
        const validStipsForCategory: IStipulation[] = stipsForCategoryAndCustomer.filter(scc => scc.appCustomerStipulations.some(acs => acs.stipulationStatusId === Constants.stipulationStatuses.verified || acs.stipulationStatusId === Constants.stipulationStatuses.exception));
        if (validStipsForCategory.length === 0) {
          errors.push(`No valid stipulations for "${stipCategory.name}" exist for the customer ${appCustomers.find(ac => ac.id === invalidGroup.appCustomerId)?.customer?.firstName} ${appCustomers.find(ac => ac.id === invalidGroup.appCustomerId)?.customer?.lastName}.`);
        }
      } else { // If it does NOT exist: add this stipulation to the group.
        errors.push(`No stipulations for "${stipCategory.name}" exist for the customer ${appCustomers.find(ac => ac.id === invalidGroup.appCustomerId)?.customer?.firstName} ${appCustomers.find(ac => ac.id === invalidGroup.appCustomerId)?.customer?.lastName}.`);
      }
    });
  });

  return errors;
};

// Validates the form when a user tries to pull credit for a customer. Retuns a list of errors,
// depending on what form values fail validation.
export const validateCreditInfo = (customer: ICustomer): string[] => {
  const errors: string[] = [];
  // Ensure user has saved app before pulling credit.
  if (customer.id === Constants.emptyGuid) {
    errors.push("No customer ID. Please save the application before pulling credit.");
  }
  // Ensure address object is not null or empty before checking properties off of it.
  if (isNil(customer.customerAddress[0]) || isEmpty(customer.customerAddress[0])) {
    errors.push("Address is required for pulling credit.");
  } else {
    if (isEmpty(customer.customerAddress[0].street1)) {
      errors.push("Street is required for pulling credit.");
    }
    if (isEmpty(customer.customerAddress[0].city)) {
      errors.push("City is required for pulling credit.");
    }
    if (isEmpty(customer.customerAddress[0].zipCode)) {
      errors.push("Zip code is required for pulling credit.");
    }
    if (isNil(customer.customerAddress[0].state) || isEmpty(customer.customerAddress[0].state) || isEmpty(customer.customerAddress[0].state.abbreviation)) {
      errors.push("State abbreviation is required for pulling credit.");
    }
  }
  // Ensure DOB exists.
  if (isEmpty(customer.dateOfBirth)) {
    errors.push("Birthdate is required for pulling credit.");
  }
  // Ensure email object is not null or empty before checking properties off of it.
  // if (isNil(customer.customerEmail[0]) || isEmpty(customer.customerEmail[0])) {
  //   errors.push("Email is required for pulling credit.");
  // } else {
  //   if (isEmpty(customer.customerEmail[0].emailAddress)) {
  //     errors.push("Email address is required for pulling credit.");
  //   }
  // }
  // Ensure employment object is not null or empty before checking properties off of it.
  if (isNil(customer.customerEmployment[0]) || isEmpty(customer.customerEmployment[0])) {
    errors.push("Employment is required for pulling credit.");
  } else {
    if (isEmpty(customer.customerEmployment[0].income)) {
      errors.push("Income is required for pulling credit.");
    }
    if (isEmpty(customer.customerEmployment[0].monthsWorked)) {
      errors.push("Months worked are required for pulling credit.");
    }
    if (isEmpty(customer.customerEmployment[0].yearsWorked)) {
      errors.push("Years worked are is required for pulling credit.");
    }

    // https://www.pivotaltracker.com/n/projects/2549833/stories/181271127/
    // if (isEmpty(customer.customerEmployment[0].phone)) {
    //   errors.push("Employment phone is required for pulling credit.");
    // }
    // if (isEmpty(customer.customerEmployment[0].companyName)) {
    //   errors.push("Company name is required for pulling credit.");
    // }
  }
  // Ensure SSN exists.
  if (isEmpty(customer.ssn.replace(/-/g, '').trim())) {
    errors.push("SSN is required for pulling credit.");
  }
  // Validate social security number.
  if (!isValidSocialSecurityNumber(customer.ssn.replace(/-/g, '').trim())) {
    errors.push("A valid SSN is required for pulling credit.");
  }
  return errors;
}



// Validates the form when a user tries to pull credit for a customer. Retuns a list of errors,
// depending on what form values fail validation.
export const validateFinicityInfo = (customer: ICustomer): string[] => {
  const errors: string[] = [];
  // Ensure user has saved app before pulling credit.
  if (customer.id === Constants.emptyGuid) {
    errors.push("No customer ID. Please save the application before using Finicity.");
  }
  // Ensure address object is not null or empty before checking properties off of it.
  if (isNil(customer.customerAddress[0]) || isEmpty(customer.customerAddress[0])) {
    errors.push("Address is required for using Finicity.");
  } else {
    if (isEmpty(customer.customerAddress[0].street1)) {
      errors.push("Street is required for using Finicity.");
    }
    if (isEmpty(customer.customerAddress[0].city)) {
      errors.push("City is required for using Finicity.");
    }
    if (isEmpty(customer.customerAddress[0].zipCode)) {
      errors.push("Zip code is required for using Finicity.");
    }
    if (isNil(customer.customerAddress[0].state) || isEmpty(customer.customerAddress[0].state) || isEmpty(customer.customerAddress[0].state.abbreviation)) {
      errors.push("State abbreviation is required for using Finicity.");
    }
  }
  // Ensure DOB exists.
  if (isEmpty(customer.dateOfBirth)) {
    errors.push("Birthdate is required for using Finicity.");
  }
  // Ensure email object is not null or empty before checking properties off of it.
  if (isNil(customer.customerEmail[0]) || isEmpty(customer.customerEmail[0])) {
    errors.push("Email is required for using Finicity.");
  } else {
    if (isEmpty(customer.customerEmail[0].emailAddress)) {
      errors.push("Email address is required for using Finicity.");
    }
  }
  // Ensure phone number object is not null or empty before checking properties off of it.
  if (!customer.customerPhone.some(p => p.phoneNumber !== "")) {
    errors.push("Phone number is required for using Finicity.");
  }
  // Ensure SSN exists.
  if (isEmpty(customer.ssn.replace(/-/g, '').trim())) {
    errors.push("SSN is required for using Finicity.");
  }
  // Validate social security number.
  if (!isValidSocialSecurityNumber(customer.ssn.replace(/-/g, '').trim())) {
    errors.push("A valid SSN is required for using Finicity.");
  }
  return errors;
}


export const validateAppRequirementsForApproval = (app: IApplication, configuration?: IConfiguration) => {
  const errors: string[] = [];

  if (configuration?.featureFlags[FeatureFlagIds.MinimumMonthlyIncome]) {
    const primary = getApplicant(app, Constants.roleApplicantId, false);
    const minMonthlyIncome = 1200;

    let totalMonthlyIncome = 0;
    for (const customerEmployment of (primary?.customer?.customerEmployment || [])) {
      totalMonthlyIncome += Number(customerEmployment?.income ?? 0);
    }

    if (totalMonthlyIncome < minMonthlyIncome) {
      errors.push(`Primary applicant's monthly income is less than $${minMonthlyIncome}.`);
    }
  }

  return errors;
}

export const validateAppRequirementsForDesking = (app: IApplication) => {
  const errors: string[] = [];
  const minimumYearsAddressHistory = 1;
  const minimumYearsEmploymentHistory = 1;

  for (const c of app.appCustomers) {
    // Check Address History
    let monthsAddressHistory = 0;
    for (const a of c.customer.customerAddress) {
      const months = (a.yearsLived || 0) * 12 + (a.monthsLived || 0);
      monthsAddressHistory += months;
    }
    if (monthsAddressHistory < (minimumYearsAddressHistory * 12)) {
      errors.push(`Customer ${c.customer.firstName} ${c.customer.lastName} does not have minimum total address history of ${minimumYearsAddressHistory} years.`);
    }

    // Check Employment History
    let monthsEmploymentHistory = 0;
    for (const e of c.customer.customerEmployment) {
      const months = (e.yearsWorked || 0) * 12 + (e.monthsWorked || 0);
      monthsEmploymentHistory += months;
    }
    if (monthsEmploymentHistory < (minimumYearsEmploymentHistory * 12)) {
      errors.push(`Customer ${c.customer.firstName} ${c.customer.lastName} does not have minimum total employment history of ${minimumYearsEmploymentHistory} years.`);
    }
  }

  return errors;
}

export const hasRole = (app: IApplication, role: string): boolean => {
  return app.appCustomers?.some(x => x.customerRole.name === role);
};

export const hasCb1Role = (app: IApplication): boolean => {
  return app.appCustomers?.some(x => x.customerRole.name === cb1RoleName);
};

export const hasCb2Role = (app: IApplication): boolean => {
  return app.appCustomers?.some(x => x.customerRole.name === cb2RoleName);
};

export const hasCobuyer = (app: IApplication): boolean => {
  return hasCb1Role(app) || hasCb2Role(app);
};
export const getApplicant = (app: IApplication | null | undefined, roleId: string, ignoreDeleted: boolean): IAppCustomer | undefined => {
  const applicant = app?.appCustomers?.find(appCustomer => (ignoreDeleted === false || appCustomer.deleted === false) && appCustomer.customerRole.id.toLowerCase() === roleId.toLowerCase());
  return applicant;
}
// export const getPrimary = (app?: IApplication): IAppCustomer | undefined => {
//   const primary = app?.appCustomers?.find(x => x.customerRole.id.toLowerCase() === Constants.roleApplicantId.toLowerCase());
//   return primary;
// }

// export const getCobuyer1 = (app?: IApplication): IAppCustomer | undefined => {
//   const cobuyer1 = app?.appCustomers?.find(x => x.customerRole.id.toLowerCase() === Constants.roleCoApplicant1Id.toLowerCase());
//   return cobuyer1;
// }

// export const getCobuyer2 = (app?: IApplication): IAppCustomer | undefined => {
//   const cobuyer2 = app?.appCustomers?.find(x => x.customerRole.id.toLowerCase() === Constants.roleCoApplicant2Id.toLowerCase());
//   return cobuyer2;
// }

export const getAge = (c?: ICustomer) => {
  if (!c?.dateOfBirth) {
    return undefined;
  } else {
    try {
      const dob = new Date(c.dateOfBirth);
      const now = new Date();
      const years = differenceInCalendarYears(now, dob);
      return years;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }
}

export const massageApp = (
  app: IApplication,
  customFields: ICustomField[]
): IApplication => {
  // do any additional data massaging here. For example, we're
  // converting these to actual dates.
  app.applicationDate = new Date(app.applicationDate);
  app.createdDate = new Date(app.createdDate);

  // Massage customer employments
  app.appCustomers.forEach(ac => {
    // If customer has one employment, then set it as current and primary.
    if (ac.customer.customerEmployment.length === 1) {
      ac.customer.customerEmployment[0].primaryEmployment = true;
      ac.customer.customerEmployment[0].isCurrent = true;
    } else {
      ac.customer.customerEmployment.forEach(ce => {
        // if the employment is primary, then set is as current.
        if (ce.primaryEmployment) {
          ce.isCurrent = true;
        }
      });
    }
  });

  // Adds the customerCustomFields or applicationCustomFields that don't exist
  for (let c of customFields) {
    if (c.isCustomerCustomField) {
      // customerCustomFields
      app.appCustomers.forEach(ac => {
        if (!ac.customer.customerCustomFields.find(ccf => ccf.customFieldId === c.id)) {
          ac.customer.customerCustomFields.push({
            id: v4(),
            customerId: ac.customerId,
            value: "",
            calculatedValue: "",
            isAutomaticallyCalculated: c.isAutomaticallyCalculated,
            customField: { ...c },
            customFieldId: c.id
          });
        }
      });
    } else {
      // applicationCustomFields
      if (!app.applicationCustomFields?.find(x => x.customFieldId === c.id)) {
        app.applicationCustomFields.push({
          id: v4(),
          applicationId: app.id,
          value: "",
          calculatedValue: "",
          isAutomaticallyCalculated: c.isAutomaticallyCalculated,
          customField: { ...c },
          customFieldId: c.id,
        });
      }
    }
  }

  // ApplicationFunding
  if (app.applicationFunding) {
    app.applicationFunding.contractDate = app.applicationFunding.contractDate ? new Date(app.applicationFunding.contractDate) : app.applicationFunding.contractDate;
    app.applicationFunding.finalDueDate = app.applicationFunding.finalDueDate ? new Date(app.applicationFunding.finalDueDate) : app.applicationFunding.finalDueDate;
    app.applicationFunding.firstDueDate = app.applicationFunding.firstDueDate ? new Date(app.applicationFunding.firstDueDate) : app.applicationFunding.firstDueDate;
    app.applicationFunding.modifiedDate = app.applicationFunding.modifiedDate ? new Date(app.applicationFunding.modifiedDate) : app.applicationFunding.modifiedDate;
  }
  return app;
};

export const handleApplicantSwap = (
  appCustomerId: string,
  dispatch: ApplicationDispatch,
  currentApp: IApplication,
  businessRulesEngine: BusinessRulesEngine
) => {
  const updatedApp = produce(currentApp, (appToUpdate) => {
    const primaryApplicant: IAppCustomer = appToUpdate.appCustomers.find(x => x.customerRole.isPrimaryApplicant)!;
    const swapApplicant: IAppCustomer = appToUpdate.appCustomers.find(x => x.id === appCustomerId)!;
    const primaryIndex: number = appToUpdate.appCustomers.findIndex(x => x.customerRole.isPrimaryApplicant)!;
    const swapIndex: number = appToUpdate.appCustomers.findIndex(x => x.id === appCustomerId)!;
    const primaryRole: ICustomerRole = primaryApplicant.customerRole;

    primaryApplicant.customerRole = { ...swapApplicant.customerRole }
    primaryApplicant.customerRoleId = swapApplicant.customerRoleId;

    swapApplicant.customerRole = { ...primaryRole };
    swapApplicant.customerRoleId = primaryRole.id;

    appToUpdate.appCustomers = [...appToUpdate.appCustomers.slice(0, primaryIndex), primaryApplicant, ...appToUpdate.appCustomers.slice(primaryIndex + 1)];
    appToUpdate.appCustomers = [...appToUpdate.appCustomers.slice(0, swapIndex), swapApplicant, ...appToUpdate.appCustomers.slice(swapIndex + 1)];
  });

  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
}

export const handleCoApplicantDelete = (
  appCustomerId: string,
  dispatch: ApplicationDispatch,
  currentApp: IApplication,
  businessRulesEngine: BusinessRulesEngine
) => {

  // Get a deep copy of the app and then update it using immer's produce function
  const updatedApp = produce(currentApp, (currentApp) => {
    const copyObject = currentApp?.appCustomers.find(x => x.id === appCustomerId);
    if (copyObject) {
      copyObject.deleted = true;
    }
  });

  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
}


export const convertToNumber = (value: string | number | undefined): number => {
  if (Number.isNaN(Number.parseFloat(value as string))) {
    return 0;
  }
  return Number.parseFloat(value as string);
}
export const getCustomFieldAllowNull = (app: IApplication, fieldName: string): IEntityCustomField | undefined => {
  const customField = app.applicationCustomFields?.find(cf => cf.customField.name === fieldName);
  if (customField === undefined) {
    //throw ("ERROR: requested custom value not found: " + fieldName);
    //throw new Error("ERROR: requested custom value not found: " + fieldName);
  }
  return customField;
}

export const getCustomField = (app: IApplication, fieldName: string): IEntityCustomField => {
  const customField = app.applicationCustomFields?.find(cf => cf.customField.name === fieldName);
  if (customField === undefined) {
    //throw ("ERROR: requested custom value not found: " + fieldName);
    throw new Error("ERROR: requested custom value not found: " + fieldName);
  }
  return customField;
}

export const hideChatBar = (clientId?: string): boolean => {
  const clientsThatDontWantChat = ['ATC'];
  return includes(clientId, clientsThatDontWantChat);
}

export const createDefaultCustomer = (mappingId: number, isPrimary: boolean): Customer => {
  return {
    mappingId: mappingId,
    isPrimary: isPrimary,
    firstName: '',
    lastName: '',
    middleName: '',
    dateOfBirth: undefined,
    cellPhone: '',
    addresses: [{
      mappingId: 0,
      isPrimary: true,
      yearsAt: null,
      monthsAt: null
    }],
    employments: [
      {
        mappingId: 0,
        isPrimary: true,
        monthsEmployed: null,
        yearsEmployed: null
      },
    ],
    email: '',
    incomeAndDebt: {},
    otherIncomeAndDebt: [],
    previousCarCredit: []
  };
}

export const isInRole = (roleId: string, employee?: IEmployee,): boolean => {
  const employeeRoleIds = (employee?.employeeEmployeeRoles ?? []).map(eer => eer.employeeRoleId.toLowerCase());
  const result = employee !== undefined && includes(roleId.toLowerCase(), employeeRoleIds);
  return result;
}

export const getAllEmployeesInRoles = (roleIds: string[], employees?: IEmployee[]): IEmployee[] => {
  const employeesInRoles: IEmployee[] = [];
  const lowercaseRoleIds = roleIds.map(r => r.toLowerCase());

  employees?.forEach(e => {
    const lowercaseEmployeeRoleIds = e.employeeEmployeeRoles?.map(eer => eer.employeeRoleId.toLowerCase());
    // If any of these match, it will add the employee to the list
    if (intersection(lowercaseEmployeeRoleIds, lowercaseRoleIds).length > 0) {
      employeesInRoles.push(e);
    }
  });

  return employeesInRoles;
}

export const getAssignedEmployeeByRoleAndSequence = (applicationAssignments: IApplicationAssignment[], roleId: string, sequence: number) => {
  return applicationAssignments ? applicationAssignments.find(aa => aa.assignmentEmployeeRoleId === roleId && aa.assignmentRoleSequence === sequence) : undefined;
}

export const getUnexcludedApplicationChecklists = (checklists: IChecklist[], applicationChecklists: IApplicationChecklist[]) => {
  return applicationChecklists.filter(ac => checklists.find(c => c.id === ac.checklistId && !c.excludeFromCompletionStatus))
};

export const getNumChecklistItemsFinished = (applicationChecklist: IApplicationChecklist | undefined): number => {
  let numFinished = 0;
  applicationChecklist?.applicationChecklistItems?.forEach(aci => {
    numFinished += (aci.checklistItemStatusId.toLowerCase() === Constants.checklistItemStatuses.completedId
      || aci.checklistItemStatusId.toLowerCase() === Constants.checklistItemStatuses.exceptionId)
      ? 1
      : 0;
  });
  return numFinished;
};

export const getFinishedChecklistItemsString = (numberOfFinishedItems: number, numberOfTotalItems: number, approvedDate: Date | undefined): string => {
  if (numberOfFinishedItems < numberOfTotalItems) {
    return `(${numberOfFinishedItems} / ${numberOfTotalItems}) Items Complete`;
  } else {
    if (approvedDate) {
      return "All Items Complete, Approved";
    } else {
      return "All Items Complete, Not Approved";
    }
  }
};

export const allApplicationChecklistsAreComplete = (checklists: IChecklist[], applicationChecklists: IApplicationChecklist[]): boolean => {
  let areAllChecklistsComplete = true;

  getUnexcludedApplicationChecklists(checklists, applicationChecklists)?.forEach(ac => {
    if (getNumChecklistItemsFinished(ac) < ac.applicationChecklistItems.length
      || !ac.approvedDate) {
      areAllChecklistsComplete = false;
    }
  });

  return areAllChecklistsComplete;
};

export const allApplicationRowChecklistsAreComplete = (applicationRowChecklists: IApplicationRowChecklist[]): boolean => {
  let areAllChecklistsComplete = true;

  applicationRowChecklists.forEach(arc => {
    if (arc.numberOfFinishedItems < arc.numberOfTotalItems
      || !arc.approvedDate) {
      areAllChecklistsComplete = false;
    }
  });

  return areAllChecklistsComplete;
};

export const canViewChecklist = (loggedInUser: IEmployee, checklistId: string, employeeRoleChecklists: IEmployeeRoleChecklist[]) => {
  let canViewThisChecklist = false;
  const checklistRoles = employeeRoleChecklists.filter(aec => aec.checklistId === checklistId)?.map(aec => aec.employeeRoleId);

  checklistRoles.forEach(cr => {
    if (isInRole(cr, loggedInUser) || isInRole(Constants.roles.administratorId, loggedInUser)) {
      canViewThisChecklist = true;
    }
  })

  return canViewThisChecklist;
};

export const canViewAnyAvailableChecklist = (
  loggedInUser: IEmployee,
  checklistIds: string[],
  employeeRoleChecklists: IEmployeeRoleChecklist[]
) => {
  let canViewAChecklist = false;
  checklistIds.forEach(checklistId => {
    if (canViewChecklist(loggedInUser, checklistId, employeeRoleChecklists)) {
      canViewAChecklist = true;
    }
  });

  return canViewAChecklist;
};

// Returns true if the array has at least one exception with this status
export const applicationExceptionsHaveStatus = (applicationExceptions: IApplicationException[] | undefined, statusIds: string[]): boolean => {
  let statusIsPresent = false;
  applicationExceptions?.forEach(ae => {
    if (statusIds.includes(ae.applicationExceptionStatusId)) {
      statusIsPresent = true;
    }
  })
  return statusIsPresent;
}

export const getStatesFilteredForClient = (states: IState[], configuration?: IConfiguration): IState[] => {
  const matchedStates: IState[] = [];
  configuration?.customerStateIdOptions?.forEach(cso => {
    const matchedState = states.find(s => s.id === cso.toLowerCase());
    if (matchedState) {
      matchedStates.push(matchedState);
    }
  });
  return sort((left: IState, right: IState) =>
    left.abbreviation > right.abbreviation ? 1
      : left.abbreviation < right.abbreviation ? -1
        : 0
    , matchedStates
  );
}

export const getLeaseQuotePaymentCalc = (applicationLeaseQuote: IApplicationLeaseQuote | undefined): number | undefined =>
  applicationLeaseQuote?.basePaymentAmount
    ? applicationLeaseQuote.basePaymentAmount + (applicationLeaseQuote?.taxPortionOfPayment ?? 0)
    : undefined;

export const getLeaseQuoteTotalOfPaymentsCalc = (applicationLeaseQuote: IApplicationLeaseQuote | undefined): number | undefined => {
  const payment = getLeaseQuotePaymentCalc(applicationLeaseQuote);
  return (payment && applicationLeaseQuote?.solutionNumberOfPmts)
    ? payment * applicationLeaseQuote.solutionNumberOfPmts
    : undefined;
}

export const getLatestException = (applicationExceptions: IApplicationException[] | undefined): IApplicationException | undefined =>
  applicationExceptions?.length
    ? applicationExceptions.reduce((first, second) =>
      first.createdDate > second.createdDate ? first : second
    )
    : undefined;

export const getRegionalManagersForSalesManager = (employees: IEmployee[], organizations: Organization[], salesManagerId: string): IEmployee[] => {
  const salesManagerOrganizationId = employees.find(e => e.id === salesManagerId)?.organizationId;
  const regionalOrganizationId = organizations.find(o => o.id === salesManagerOrganizationId)?.parentOrganizationId;
  return employees.filter(e => e.organizationId === regionalOrganizationId && isInRole(Constants.roles.regionalManagerId, e));
}

export const updateApplicationCustomFields = (customFieldsToUpdate: ICustomValuePair[], currentApp: IApplication, dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine) => {
  const updatedApp = produce(currentApp, (updatedCurrentApp) => {
    customFieldsToUpdate.forEach(cf => {
      const applicationCustomField = updatedCurrentApp?.applicationCustomFields?.find(x => x.customField.name === cf.customFieldName);
      if (applicationCustomField) {
        applicationCustomField[cf.valueType] = cf.formattedValue;
      }
    })
  });
  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
};

export const updateCustomerCustomFields = (customerId: string, customFieldsToUpdate: ICustomValuePair[], currentApp: IApplication, dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine) => {
  const updatedApp = produce(currentApp, (updatedCurrentApp) => {
    const customer = updatedCurrentApp.appCustomers.find(ac => ac.customerId === customerId)?.customer;
    if (customer) {
      customFieldsToUpdate.forEach(cf => {
        const customerCustomField = customer.customerCustomFields.find(ccf => ccf.customField.name === cf.customFieldName);
        if (customerCustomField) {
          customerCustomField[cf.valueType] = cf.formattedValue;
        }
      })
    }
  });
  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
};


export const applicationIsPastDesking = (currentAppStatus: string | undefined): boolean => {
  return hasValue(currentAppStatus) && applicationStatusesAfterDeskingToAms.includes(currentAppStatus!.toUpperCase());
}

export const applicationCanBeReDeskedInStatus = (applicationStatus: string, configuration?: IConfiguration): boolean => {
  // If (the list's length is empty or doesn't exist) or the application's status is re-desk-able
  return !configuration?.reDeskStatuses?.length || configuration?.reDeskStatuses.includes(applicationStatus);
}

export const getRuleViolationsByType = (
  currentApp: IApplication | undefined,
  financeQuoteErrors: IRuleViolation[],
  ruleViolationType: 'information' | 'warning' | 'error' | 'auto-deny-on-submit'
): IRuleViolation[] => {
  const ruleViolations: IRuleViolation[] = [];
  if (currentApp) {
    currentApp.applicationQuotes.filter(aq => aq.quote.quoteType === IndirectQuoteType.Underwriter && aq.ruleViolations !== undefined)
      .forEach(aq => {
        const errors = aq.ruleViolations?.filter(rv => rv.type === ruleViolationType);
        if (errors) {
          ruleViolations.push(...errors);
        }
      });
  }

  const financeQuoteAutoDenyErrors = financeQuoteErrors.filter(rv => rv.type === ruleViolationType);
  if (financeQuoteAutoDenyErrors) {
    ruleViolations.push(...financeQuoteAutoDenyErrors);
  }

  return ruleViolations;
}

export const getIndirectSubmitToButtonText = (applicationSource: string | undefined): string => {
  if (applicationSource === undefined) {
    return '';
  }

  switch (applicationSource) {
    case "Dealer Flow":
      return "Dealer";
    default:
      return applicationSource.trim();
  }
}

// Returns true if all the appCustomers have accepted the terms and conditions, otherwise false.
export const haveAllCustomersAcceptedTermsConditions = (currentApp: IApplication | undefined): boolean => {
  return isNotNil(currentApp) && currentApp.appCustomers.every(c => hasValue(c.customer.customerCredit[0]?.acknowledgementDate));
}

// calculates the job time of the primary employment. Format is 'x yrs, y mos'.
export const getPrimaryJobTime = (customerEmployments: ICustomerEmployment[], applicationDate: Date): string => {
  const primaryEmployment = customerEmployments.find(ce => ce.primaryEmployment);
  if (primaryEmployment) {
    const monthsElapsed = getMonthsElapsed(primaryEmployment.startDate, primaryEmployment.endDate, primaryEmployment.isCurrent, applicationDate);
    const years = Math.floor(monthsElapsed / 12);
    const months = monthsElapsed % 12;
    return months >= 12 ? `${years + Math.floor(months / 12)} yrs, ${months % 12} mos` : `${years} yrs, ${months} mos`;
  }
  return '';
}

// calculates the job time of the primary employment and returns the time in months
export const getPrimaryJobTimeInMonths = (customerEmployments: ICustomerEmployment[], applicationDate: Date): number => {
  const primaryEmployment = customerEmployments.find(ce => ce.primaryEmployment);
  if (primaryEmployment) {
    return getMonthsElapsed(primaryEmployment.startDate, primaryEmployment.endDate, primaryEmployment.isCurrent, applicationDate);
  }
  return 0;
}

// calculates the total job time for the given list of customer employments. Format is 'x yrs, y mos'.
export const getTotalJobTime = (customerEmployments: ICustomerEmployment[], applicationDate: Date): string => {
  let totalYears = 0;
  let totalMonths = 0;
  customerEmployments.forEach(ce => {
    const monthsElapsed = getMonthsElapsed(ce.startDate, ce.endDate, ce.isCurrent, applicationDate);
    totalYears += Math.floor(monthsElapsed / 12);
    totalMonths += monthsElapsed % 12;
  });
  return totalMonths >= 12 ? `${totalYears + Math.floor(totalMonths / 12)} yrs, ${totalMonths % 12} mos` : `${totalYears} yrs, ${totalMonths} mos`;
}

// calculates the residence time of the primary address. Format is 'x yrs, y mos'.
export const getCurrentResidenceTime = (customerAddresses: ICustomerAddress[], applicationDate: Date): string => {
  const currentAddress = customerAddresses.find(ca => isNil(ca.endDate))
  if (currentAddress) {
    const monthsElapsed = getMonthsElapsed(currentAddress.startDate, currentAddress.endDate, currentAddress.default, applicationDate);
    const years = Math.floor(monthsElapsed / 12);
    const months = monthsElapsed % 12;
    return months >= 12 ? `${years + Math.floor(months / 12)} yrs, ${months % 12} mos` : `${years} yrs, ${months} mos`;
  }
  return '';
}

// calculates the residence time of the primary address and returns the time in months
export const getCurrentResidenceTimeInMonths = (customerAddresses: ICustomerAddress[], applicationDate: Date): number => {
  const currentAddress = customerAddresses.find(ca => isNil(ca.endDate))
  if (currentAddress) {
    return getMonthsElapsed(currentAddress.startDate, currentAddress.endDate, currentAddress.default, applicationDate);
  }
  return 0;
}

// calculates the total residence time for the given list of customer addresses. Format is 'x yrs, y mos'.
export const getTotalResidenceTime = (customerAddresses: ICustomerAddress[], applicationDate: Date): string => {
  let totalYears = 0;
  let totalMonths = 0;
  customerAddresses.forEach(ca => {
    const monthsElapsed = getMonthsElapsed(ca.startDate, ca.endDate, ca.default, applicationDate);
    totalYears += Math.floor(monthsElapsed / 12);
    totalMonths += monthsElapsed % 12;
  });
  return totalMonths >= 12 ? `${totalYears + Math.floor(totalMonths / 12)} yrs, ${totalMonths % 12} mos` : `${totalYears} yrs, ${totalMonths} mos`;
}

/**
 * Checks if any of the required checklists (by ID from config) are incomplete.
 * A checklist is considered incomplete if it has no approver signature or no approved date.
 */
export const isAnyRequiredChecklistIncomplete = (
  requiredChecklistIds: string[],
  applicationChecklists: IApplicationChecklist[] | undefined
): boolean => {
  return requiredChecklistIds.some(requiredChecklistId => {
    const matchedChecklist = applicationChecklists?.find(
      ac => ac.checklistId.toUpperCase() === requiredChecklistId.toUpperCase()
    );
    return !hasValue(matchedChecklist?.approversSignature) || !hasValue(matchedChecklist?.approvedDate);
  });
}